import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from '@/components/ui'
import { useAuthStore } from '@/store'
import { useRoyaltyAccess } from '@/hooks/useRoyaltyAccess'
import { useManagementCompanySimple } from '@/hooks/useManagementCompanySimple'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import { useNetworkPlanningAccess } from '@/hooks/useNetworkPlanningAccess'
import { usePedagogyAccess } from '@/hooks/usePedagogyAccess'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import {
  UserCog,
  UserCheck,
  BookOpen,
  ClipboardCheck,
  FileText,
  DollarSign,
  ChevronRight,
  LayoutGrid,
  CalendarClock,
  ServerCog,
  ShieldAlert,
  Users,
  UserPlus,
  ClipboardList,
  ListChecks,
  BarChart3,
  type LucideIcon,
} from 'lucide-react'

type HubSection = {
  path: string
  icon: LucideIcon
  title: string
  description: string
  enabled: boolean
}

export function ManagementHubPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { hasPermission: hasRoyaltyPermission } = useRoyaltyAccess(user?.id)
  const { getAccessLevel } = useManagementCompanySimple(user?.id)
  const { isSuperAdmin } = useSuperAdmin()
  const { canViewNetworkPlanning } = useNetworkPlanningAccess()
  const { canViewPedagogySection } = usePedagogyAccess()
  const [isAdvanced, setIsAdvanced] = useState(false)

  useEffect(() => {
    let cancelled = false

    if (!user?.id) {
      setIsAdvanced(false)
      return
    }

    getAccessLevel().then(level => {
      if (!cancelled) setIsAdvanced(level === 'advanced')
    })

    return () => {
      cancelled = true
    }
  }, [user?.id, getAccessLevel])

  // Доступы — те же, что в нижней навигации УК. Меняем позже централизованно.
  const sections = useMemo<HubSection[]>(
    () => [
      {
        path: '/management/territorial-managers',
        icon: UserCog,
        title: 'Территориальные управляющие',
        description: 'Дашборд по периодам, назначение франчайзи, KPI',
        enabled: true,
      },
      {
        path: '/management/personal-managers',
        icon: UserCheck,
        title: 'Личные менеджеры',
        description: 'Дневной чек-лист по студиям, назначения, выходные',
        enabled: true,
      },
      {
        path: '/management/shmp',
        icon: BookOpen,
        title: 'ШМП',
        description: 'Школа молодого предпринимателя: курсы и участники',
        enabled: true,
      },
      {
        path: pedagogyPaths.department,
        icon: ClipboardCheck,
        title: 'Педагогический отдел',
        description: 'Педагоги, обучение, договоры, визитки и отсмотры',
        enabled: canViewPedagogySection,
      },
      {
        path: '/management/documents-registry',
        icon: FileText,
        title: 'Юридический отдел',
        description: 'Реестр документов по ДКК студий',
        enabled: true,
      },
      {
        path: '/management/studio-closures',
        icon: CalendarClock,
        title: 'Закрытие студий',
        description: 'Подготовка к закрытию, сроки и уведомления владельцев',
        enabled: isAdvanced,
      },
      {
        path: '/management/crm-accounts',
        icon: ServerCog,
        title: 'CRM-аккаунты',
        description: 'Передачи студий, привязки Листка, контроль дублей',
        enabled: isAdvanced,
      },
      {
        path: '/management/missing-payments',
        icon: ShieldAlert,
        title: 'Аудит синхронизации платежей',
        description: 'Наблюдения по выгрузке ListokCRM: пропажи, исключения из выручки, платежи без филиала',
        enabled: isAdvanced,
      },
      {
        path: '/royalties',
        icon: DollarSign,
        title: 'Роялти',
        description: 'Начисления, оплаты и долги по франчайзи и студиям',
        enabled: hasRoyaltyPermission('view'),
      },
      {
        path: '/management/network-planning',
        icon: BarChart3,
        title: 'Экран планирования сети',
        description: 'Годовая выручка, начисления, собираемость и движение студий',
        enabled: canViewNetworkPlanning === true,
      },
      {
        path: '/management/original',
        icon: ClipboardList,
        title: 'Реестр студий и франчайзи',
        description: 'История исходных данных, изменения и передача кейсов ответственным подразделениям',
        enabled: isAdvanced,
      },
      {
        path: '/management/original/cases',
        icon: ClipboardList,
        title: 'Кейсы',
        description: 'Обработка информации, переданной из раздела «Реестр студий и франчайзи»',
        enabled: isSuperAdmin === true,
      },
      {
        path: '/management/access',
        icon: UserPlus,
        title: 'Заявка на доступ в УК',
        description: 'Попросить завести сотрудника — решение принимает супер-админ',
        // Открыто всем сотрудникам УК: заявка и заведена ради тех, у кого нет кнопок.
        enabled: true,
      },
      {
        path: '/management/work',
        icon: ListChecks,
        title: 'Запросы в разработку',
        description: 'Попросить доработку и посмотреть, что с ней',
        // Заказать и смотреть может любой сотрудник УК; менять — только команда (сервер).
        enabled: true,
      },
      {
        path: '/management/users',
        icon: Users,
        title: 'Пользователи',
        description: 'Контакты и способы входа, попытки авторизации, активность',
        // Раздел с персональными данными всей сети — только супер-админ, не любой advanced
        enabled: isSuperAdmin === true,
      },
    ],
    [canViewNetworkPlanning, canViewPedagogySection, hasRoyaltyPermission, isAdvanced, isSuperAdmin]
  )

  const visible = sections.filter(s => s.enabled)

  // TODO (позже): если доступен ровно один раздел — редиректить сразу на него,
  // минуя экран выбора направления.

  return (
    <div className="space-y-4 pb-24">
      <div className="flex items-center gap-2">
        <LayoutGrid size={20} className="text-primary" />
        <h1 className="text-lg font-semibold">Направления</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {visible.map(section => {
          const Icon = section.icon
          return (
            <button
              key={section.path}
              onClick={() => navigate(section.path)}
              className="text-left"
            >
              <Card className="h-full transition-colors hover:bg-accent/50">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-primary/10 p-2 text-primary shrink-0">
                    <Icon size={22} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-sm">{section.title}</div>
                      <ChevronRight size={16} className="text-muted-foreground shrink-0" />
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{section.description}</div>
                  </div>
                </div>
              </Card>
            </button>
          )
        })}
      </div>
    </div>
  )
}
